import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import * as XLSX from "xlsx";
import { parseAmbulantniDnevnik } from "../utils/parseAmbulantniDnevnik";
import logger from "../../../../utils/logger";
import { useTranslation } from "react-i18next";

const FILE_FIELD_NAME = "files";

export function useSpreadsheetImport() {
  const { watch } = useFormContext();
  const selectedFiles = watch(FILE_FIELD_NAME);
  const file = Array.isArray(selectedFiles) ? selectedFiles[0] : selectedFiles;
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [excludedIds, setExcludedIds] = useState(() => new Set());

  useEffect(() => {
    setStatus(file ? "ready" : "idle");
    setError(null);
  }, [file]);

  const parseFile = useCallback(async () => {
    if (!file) return;
    setStatus("loading");
    setError(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        raw: true,
      });

      const parsedUsers = parseAmbulantniDnevnik(rows);
      setUsers(parsedUsers);
      setExcludedIds(new Set());
      setStatus("done");
    } catch (err) {
      logger.error(err);
      setError(t(err.message) || t("SomethingWentWrong"));
      setStatus("ready");
    }
  }, [file]);

  const updateUser = useCallback((updatedUser) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
    );
  }, []);

  // Drops rows that were imported, so only the ones still to import stay in the table
  const removeUsers = useCallback((userIds) => {
    const ids = new Set(userIds);
    setUsers((prev) => prev.filter((u) => !ids.has(u.id)));
    setExcludedIds((prev) => new Set([...prev].filter((id) => !ids.has(id))));
  }, []);

  const toggleUser = useCallback((userId) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const removeAllUsers = useCallback(() => {
    setExcludedIds(new Set(users.map((u) => u.id)));
  }, [users]);

  const addAllUsers = useCallback(() => {
    setExcludedIds(new Set());
  }, []);

  const includedUsers = useMemo(
    () => users.filter((u) => !excludedIds.has(u.id)),
    [users, excludedIds],
  );

  const reset = useCallback(() => {
    setUsers([]);
    setExcludedIds(new Set());
    setStatus(file ? "ready" : "idle");
    setError(null);
  }, [file]);

  return {
    status, // "idle" | "ready" | "loading" | "done"
    error,
    file,
    users,
    includedUsers,
    excludedIds,
    toggleUser,
    updateUser,
    removeUsers,
    removeAllUsers,
    addAllUsers,
    parseFile,
    reset,
  };
}
